import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import {
  Tabs, Table, Button, Select, Input, Popconfirm,
  notification, Empty, Card, Space, Modal, Form, DatePicker as AntDatePicker
} from 'antd';
import {
  DownOutlined, UpOutlined, PlusOutlined,
  CalendarOutlined, TagsOutlined, FileTextOutlined
} from '@ant-design/icons';
// ❌ remove react-datepicker from the table use-case
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
import '../styles/JobPage.css';
import JobDrive from './JobDrive';

const { TabPane } = Tabs;
const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const STATUS_OPTIONS = ['Pitching', 'On-Process', 'Cancel', 'Done'];

/* ---------- helpers ---------- */
const toDayjs = (d) => (d ? dayjs(d) : null);
const fromDayjs = (dj) => (dj ? dj.toDate() : null);

/* ---------- inline editors ---------- */
const EditableSelectCell = React.memo(function EditableSelectCell({ initialValue, options, onChange }) {
  const [val, setVal] = useState(initialValue);
  useEffect(() => { setVal(initialValue); }, [initialValue]);
  return (
    <Select
      value={val}
      onChange={(v) => { setVal(v); onChange?.(v); }}
      style={{ width: '100%' }}
      getPopupContainer={() => document.body}
    >
      {options.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
    </Select>
  );
});

/** Use Antd's DatePicker so the popup renders to body and avoids clipping */
const EditableDateCell = React.memo(function EditableDateCell({ initialValue, onChange }) {
  const [val, setVal] = useState(toDayjs(initialValue));
  useEffect(() => { setVal(toDayjs(initialValue)); }, [initialValue]);

  return (
    <AntDatePicker
      value={val}
      onChange={(dj) => { setVal(dj); onChange?.(fromDayjs(dj)); }}
      format="YYYY-MM-DD"
      placeholder="Select date"
      style={{ width: '100%' }}
      suffixIcon={<CalendarOutlined />}
      // render dropdown to body so it’s not clipped by table scroll/overflow
      getPopupContainer={() => document.body}
      popupClassName="job-ant-picker-dropdown"
    />
  );
});

/** Keep typing local; commit on blur/Enter (no parent updates per keystroke) */
const EditableTextCell = React.memo(function EditableTextCell({ initialValue, onCommit, placeholder }) {
  const [val, setVal] = useState(initialValue || '');
  useEffect(() => { setVal(initialValue || ''); }, [initialValue]);

  const commit = () => onCommit?.(val.trim());
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      e.currentTarget.blur();
    }
  };

  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      prefix={<FileTextOutlined />}
    />
  );
});

/* ------------------------------------------------------------------- */

const getDefaultYearState = () => ({
  rows: [],
  loading: false,
  page: 1,
  pageSize: 10,
  total: 0,
  q: '',
  status: undefined,
});

const Job = () => {
  const [years, setYears] = useState([]);
  const [expandedYearKeys, setExpandedYearKeys] = useState([]);
  const [yearState, setYearState] = useState({});
  const [activeTab, setActiveTab] = useState('table');

  // Add modal
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm] = Form.useForm();

  const loadYears = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jobs/years`, { withCredentials: true });
      const items = (res.data || []).sort((a, b) => b.year - a.year);
      setYears(items);
      if (items.length === 0) setExpandedYearKeys([]);
    } catch {
      setYears([]);
    }
  };

  const loadYearData = async (year, overrides = {}) => {
    setYearState(prev => ({
      ...prev,
      [year]: { ...(prev[year] || getDefaultYearState()), loading: true, ...overrides }
    }));

    try {
      const current = { ...(yearState[year] || getDefaultYearState()), ...overrides };
      const params = new URLSearchParams({
        page: String(current.page),
        limit: String(current.pageSize),
      });
      if (current.q) params.set('q', current.q);
      if (current.status) params.set('status', current.status);

      const res = await axios.get(
        `${API_BASE_URL}/api/jobs/by-year/${year}?${params.toString()}`,
        { withCredentials: true }
      );
      const { data, total, page, limit } = res.data || {};

      const rows = (data || []).map(j => ({
        key: j._id,
        _id: j._id,
        jobNumber: j.jobNumber,
        jobName: j.jobName || '',
        status: j.status,
        eventDate: j.eventDate ? new Date(j.eventDate) : null,
      }));

      setYearState(prev => ({
        ...prev,
        [year]: {
          ...(prev[year] || getDefaultYearState()),
          ...current,
          rows,
          total: total || 0,
          page: page || current.page,
          pageSize: limit || current.pageSize,
          loading: false,
        }
      }));
    } catch {
      setYearState(prev => ({
        ...prev,
        [year]: { ...(prev[year] || getDefaultYearState()), loading: false }
      }));
      notification.error({ message: `Failed to load jobs for ${year}` });
    }
  };

  useEffect(() => { loadYears(); }, []);

  const onExpand = (expanded, record) => {
    const y = record.year;
    setExpandedYearKeys(expanded ? [y] : []);
    if (expanded) loadYearData(y);
  };

  // patch a row in-place (not per keystroke for text cell)
  const patchRow = (year, key, patch) => {
    setYearState(prev => {
      const s = prev[year] || getDefaultYearState();
      const rows = (s.rows || []).map(r => r.key === key ? { ...r, ...patch } : r);
      return { ...prev, [year]: { ...s, rows } };
    });
  };

  // read the latest row by key
  const getRowLatest = (year, key) => {
    const s = yearState[year] || getDefaultYearState();
    return (s.rows || []).find(r => r.key === key);
  };

  const saveRowLatest = async (year, key) => {
    // blur current input & flush so onBlur commits before we read state
    flushSync(() => {
      if (typeof document !== 'undefined' && document.activeElement) {
        document.activeElement.blur();
      }
    });

    const row = getRowLatest(year, key);
    if (!row) {
      notification.error({ message: 'Row not found.' });
      return;
    }
    const payload = {
      jobId: row._id,
      jobName: (row.jobName || '').trim(),
      status: row.status,
      eventDate: row.eventDate ? new Date(row.eventDate).toISOString() : null,
    };
    if (!payload.jobName || !payload.eventDate) {
      notification.error({ message: 'Please enter job name and date.' });
      return;
    }

    const eventYear = new Date(payload.eventDate).getFullYear();

    try {
      const res = await axios.put(`${API_BASE_URL}/api/jobs/save`, payload, { withCredentials: true });
      const saved = res.data?.job;
      notification.success({ message: payload.jobId ? `Updated Job #${saved.jobNumber}` : `Created Job #${saved.jobNumber}` });

      await loadYears();
      setExpandedYearKeys([eventYear]);
      await loadYearData(eventYear);
    } catch {
      notification.error({ message: 'Save failed.' });
    }
  };

  const deleteRow = async (year, record) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/jobs/${record._id}`, { withCredentials: true });
      notification.success({ message: `Deleted Job #${record.jobNumber}` });
      await loadYears();
      await loadYearData(year);
    } catch {
      notification.error({ message: 'Delete failed.' });
    }
  };

  const openAddModal = () => {
    setIsAddOpen(true);
    setTimeout(() => {
      addForm.resetFields();
      addForm.setFieldsValue({ status: 'Pitching', eventDate: null, jobName: '' });
    }, 0);
  };
  const closeAddModal = () => setIsAddOpen(false);

  const toISOStringSafe = (d) => {
    if (!d) return null;
    const js = typeof d?.toDate === 'function' ? d.toDate() : (d?.$d ? d.$d : d);
    return new Date(js).toISOString();
  };

  const submitAdd = async () => {
    try {
      const values = await addForm.validateFields();
      const payload = {
        jobName: (values.jobName || '').trim(),
        status: values.status,
        eventDate: toISOStringSafe(values.eventDate),
      };
      if (!payload.jobName || !payload.eventDate) {
        return notification.error({ message: 'Please enter job name and date.' });
      }
      const res = await axios.put(`${API_BASE_URL}/api/jobs/save`, payload, { withCredentials: true });
      const saved = res.data?.job;
      notification.success({ message: `Created Job #${saved.jobNumber}` });
      setIsAddOpen(false);

      await loadYears();
      const y = new Date(payload.eventDate).getFullYear();
      setExpandedYearKeys([y]);
      await loadYearData(y);
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: 'Save failed.' });
    }
  };

  const YearExpanded = React.memo(({ year, state, onReload }) => {
    const s = state || getDefaultYearState();
    const [localQ, setLocalQ] = useState(s.q || '');
    const [localStatus, setLocalStatus] = useState(s.status);

    const doSearch = () => onReload({ q: localQ, status: localStatus, page: 1 });
    const doReset = () => {
      setLocalQ('');
      setLocalStatus(undefined);
      onReload({ q: '', status: undefined, page: 1 });
    };

    const handleConfirm = useCallback(async (row) => {
      await saveRowLatest(year, row.key);
      onReload();
    }, [year, onReload]);

    const handleDelete = useCallback(async (row) => {
      await deleteRow(year, row);
      onReload();
    }, [year, onReload]);

    const columns = useMemo(() => ([
      { title: 'Job Number', dataIndex: 'jobNumber', key: 'jobNumber', width: 120, render: (v) => <div>{v}</div> },
      {
        title: 'Job Name',
        dataIndex: 'jobName',
        key: 'jobName',
        render: (_, row) => (
          <EditableTextCell
            initialValue={row.jobName}
            placeholder="Type job name"
            onCommit={(v) => patchRow(year, row.key, { jobName: v })}
          />
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (_, row) => (
          <EditableSelectCell
            initialValue={row.status}
            options={STATUS_OPTIONS}
            onChange={(v) => patchRow(year, row.key, { status: v })}
          />
        )
      },
      {
        title: 'Event Date',
        dataIndex: 'eventDate',
        key: 'eventDate',
        width: 200,
        onCell: () => ({ style: { overflow: 'visible' } }),
        render: (_, row) => (
          <EditableDateCell
            initialValue={row.eventDate}
            onChange={(d) => patchRow(year, row.key, { eventDate: d })}
          />
        )
      },
      {
        title: 'Action',
        key: 'action',
        width: 220,
        render: (_, row) => (
          <Space>
            <Button type="primary" onClick={() => handleConfirm(row)}>Confirm</Button>
            <Popconfirm title="Delete this job?" onConfirm={() => handleDelete(row)} okText="Yes" cancelText="No">
              <Button danger type="link">Delete</Button>
            </Popconfirm>
          </Space>
        ),
      },
    ]), [handleConfirm, handleDelete, year]);

    return (
      <div className="job-year-expanded">
        <div className="job-toolbar">
          <Input
            placeholder="Search by Job No. or Job Name"
            value={localQ}
            onChange={(e) => setLocalQ(e.target.value)}
            onPressEnter={doSearch}
            className="job-toolbar-input"
            allowClear
          />
          <Select
            placeholder="Status"
            value={localStatus}
            onChange={setLocalStatus}
            allowClear
            className="job-toolbar-select"
            getPopupContainer={() => document.body}
          >
            {STATUS_OPTIONS.map(x => <Option key={x} value={x}>{x}</Option>)}
          </Select>
          <Button onClick={doSearch}>Search</Button>
          <Button onClick={doReset}>Reset</Button>
        </div>

        <Table
          columns={columns}
          dataSource={s.rows || []}
          rowKey="key"
          loading={s.loading}
          locale={{ emptyText: <Empty description="No jobs yet." /> }}
          pagination={{
            current: s.page,
            pageSize: s.pageSize,
            total: s.total,
            onChange: (p, ps) => onReload({ page: p, pageSize: ps }),
          }}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
          className="job-table-allow-overflow"
        />
      </div>
    );
  });

  const outerColumns = useMemo(() => ([
    { title: 'Year', dataIndex: 'year', key: 'year', render: (y) => <div>{y}</div> },
    { title: 'Total', dataIndex: 'total', key: 'total', width: 120 },
  ]), []);

  return (
    <main className="job-page">
      <div className="job-page-header">
        <h1>JOB</h1>
        {activeTab === 'table' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
            Add Job
          </Button>
        )}
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="JOB Table" key="table">
          {years.length === 0 ? (
            <Card>
              <div className="job-empty-header">
                <h3 className="job-empty-title">Jobs</h3>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                  Add Job
                </Button>
              </div>
              <div style={{ marginTop: 16 }}>
                <Empty description="No jobs yet." />
              </div>
            </Card>
          ) : (
            <Table
              columns={outerColumns}
              dataSource={years}
              rowKey="year"
              pagination={false}
              expandable={{
                expandedRowRender: (record) => (
                  <YearExpanded
                    year={record.year}
                    state={yearState[record.year]}
                    onReload={(overrides = {}) => loadYearData(record.year, overrides)}
                  />
                ),
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? <UpOutlined onClick={(e) => onExpand(record, e)} /> : <DownOutlined onClick={(e) => onExpand(record, e)} />,
                expandedRowKeys: expandedYearKeys,
                onExpand,
              }}
              scroll={{ x: 'max-content' }}
              tableLayout="auto"
              style={{ width: '100%' }}
            />
          )}
        </TabPane>

        <TabPane tab="Job Drive" key="drive">
          <JobDrive />
        </TabPane>
      </Tabs>

      {/* Add Job Modal */}
      <Modal
        open={isAddOpen}
        onCancel={closeAddModal}
        onOk={submitAdd}
        okText="Create"
        title="Add Job"
        destroyOnClose
        maskClosable={false}
        rootClassName="job-modal"
        getContainer={false}
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{ status: 'Pitching' }}
          className="job-add-form-modern"
        >
          <div className="job-add-grid">
            <Form.Item
              name="jobName"
              label="Job Name"
              rules={[{ required: true, message: 'Please enter a job name' }]}
              extra="This exact name will be used as the Drive folder name."
              className="job-form-span-full"
            >
              <Input size="large" placeholder="e.g., ACME 2025 Campaign" prefix={<FileTextOutlined />} />
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              extra="Workflow state of the job."
            >
              <Select
                size="large"
                placeholder="Select status"
                suffixIcon={<DownOutlined />}
                getPopupContainer={() => document.querySelector('.job-modal') || document.body}
                style={{ width: '100%' }}
              >
                {STATUS_OPTIONS.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item
              name="eventDate"
              label="Event Date"
              rules={[{ required: true, message: 'Please pick a date' }]}
              extra="Used to categorize by year automatically."
            >
              <AntDatePicker
                size="large"
                style={{ width: '100%' }}
                placeholder="Select date"
                suffixIcon={<CalendarOutlined />}
                format="YYYY-MM-DD"
                getPopupContainer={() => document.querySelector('.job-modal') || document.body}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </main>
  );
};

export default Job;
